import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
    Anchor,
    atoll,
    Banner,
    Button,
    breakpoints,
    dustGray,
    FormContainer,
    Heading3,
    Heading4,
    java
} from '@parchment/atoms';
import {
    ComposedTextField,
    Form,
    RequiredAsterisk,
    VALIDATION_STATES
} from '@parchment/forms';
import { CancelIcon, NewMessageIcon } from '@parchment/icons';
import BaseLayout from './BaseLayout';
import useEmailVerification from '../common/hooks/EmailVerificationHooks';
import useAuthenticateUser from '../common/hooks/AuthenticateUserHooks';

export default function AccountConfirmationPage ({
    params = {
        code: '',
        memberId: ''
    },
    verifying = false,
    resendEmail = false
}) {
    const { authenticationInfo: sessionAuthentication, loggedOut, logoutUser } = useAuthenticateUser();
    const { verificationCode, requestVerification, verifyConfirmationCode,
        status: emailVerificationStatus, verificationResponse } = useEmailVerification();
    const [ formStatus, setFormStatus ] = useState({
        showBanner: false,
        bannerMessage: '',
        context: 'error',
        verifying: verifying,
        resendEmail: resendEmail,
        verificationCode: params?.code || '',
        isCodeValid: false
    });
    const { verifying: formVerifying, resendEmail: sendingEmail } = formStatus;
    const [ userInfo, setUserInfo ] = useState({
        id: window.config.individualId || '',
        individualEmailId: window.config.individualEmailId || '',
        email: window.config.email || '',
        hasAuthenticationInfo: false
    });

    useEffect(() => {
        if (sessionAuthentication?.authenticated && !userInfo.hasAuthenticationInfo) {
            setUserInfo({
                id: sessionAuthentication.user.id,
                individualEmailId: sessionAuthentication.individualEmailId,
                email: sessionAuthentication.user.emailAddress,
                hasAuthenticationInfo: true
            });
        }
    }, [sessionAuthentication]);

    useEffect(() => {
        if (formVerifying) {
            const verificationCodeParams = {
                individualId: userInfo.id,
                email: userInfo.email,
                code: formStatus.verificationCode,
                memberId: params?.memberId
            };
            verifyConfirmationCode(verificationCodeParams);
        }
    }, [formVerifying]);

    useEffect(() => {
        if (sendingEmail) {
            requestVerification(userInfo.individualEmailId);
        }
    }, [sendingEmail]);

    useEffect(() => {
        if (formVerifying && verificationCode?.codeVerified) {
            window.location.href = verificationResponse?.data?.redirect || `/u/auth/login?memberId=${params.memberId}`;
        }
        if (sendingEmail && verificationCode?.sentEmail) {
            setFormStatus((prevState) => ({
                ...prevState,
                showBanner: true,
                bannerMessage: 'Successfully resend verification email',
                context: 'success',
                resendEmail: false
            }));
        }
    }, [verificationCode]);

    useEffect(() => {
        if (loggedOut) {
            window.location.href = `/u/registration/${params.memberId}/institution`;
        }
    }, [loggedOut]);

    useEffect(() => {
        if (formVerifying && emailVerificationStatus?.hasError) {
            setFormStatus((prevState) => ({
                ...prevState,
                showBanner: true,
                bannerMessage: emailVerificationStatus.errorMessage,
                context: 'error',
                verifying: false
            }));
        }
    }, [emailVerificationStatus]);

    const handleResendEmail = () => {
        setFormStatus((prevState) => ({
            ...prevState,
            resendEmail: true
        }));
    };

    const handleSubmit = () => {
        setFormStatus((prevState) => ({
            ...prevState,
            verifying: true
        }));
    };

    const renderCancelLink = () => {
        return (
            <CancelLinkWrapper>
                <Anchor className='cancel-verification-link'
                        onClick={() => logoutUser()}>
                    Cancel <CancelIcon />
                </Anchor>
            </CancelLinkWrapper>
        );
    };

    return (
        <BaseLayout titleText='Account Confirmation'
                    rightComponent={renderCancelLink()}
                    showMemberBanner={false}
                    showFooter={false}>
            <AccountConfirmationInputContainer context='ghost'
                                               className='account-confirmation-input-section'>
                <div className='account-confirmation-title-text'>
                    <StyledHeading4>To create your account, we need to confirm a</StyledHeading4>
                    <StyledHeading3>valid email address</StyledHeading3>
                </div>
                <NewMessageIconWrapper>
                    <NewMessageIcon className='new-message-icon' />
                </NewMessageIconWrapper>
                <StyledHeading3 className='account-confirmation-info-header'>
                    We just sent you a confirmation email.
                </StyledHeading3>
                <div className='check-email-text'>
                    To gain access to your account, please check <strong>{userInfo.email}</strong> and
                    enter the provided registration code below.
                </div>
                <ConfirmationCodeForm id='ConfirmationCodeForm' scale='small' size='small' onSubmit={() => handleSubmit()}>
                    <ConfirmationCodeInputWrapper>
                        <ComposedTextField className='confirmation-code-input'
                                           name='code'
                                           label='Confirmation Code'
                                           autoComplete='code'
                                           internalLabel={true}
                                           value={formStatus.verificationCode}
                                           onChange={(value) => setFormStatus((prevState) => ({
                                               ...prevState,
                                               verificationCode: value
                                           }))}
                                           onValidate={(validationState) => setFormStatus((prevState) => ({
                                               ...prevState,
                                               isCodeValid: validationState === VALIDATION_STATES.success
                                           }))}
                                           required />
                        <SubmitButton name='submit'
                                      type='submit'
                                      value='Submit'
                                      className='verify-code-button'
                                      context='primary'
                                      disabled={!formStatus.isCodeValid}>
                            Submit
                        </SubmitButton>
                    </ConfirmationCodeInputWrapper>
                </ConfirmationCodeForm>
            </AccountConfirmationInputContainer>
            <FormContainer context='light'
                           className='account-confirmation-action-section'>
                {
                    formStatus.showBanner && <FormContainer.Prefix className='banner-container'>
                        <Banner visible={formStatus.showBanner} title={formStatus.context} context={formStatus.context} dismissable={true}
                                size='large' message={formStatus.bannerMessage}
                                onDismiss={() => setFormStatus((prevState) => ({
                                    ...prevState,
                                    showBanner: false,
                                    bannerMessage: ''
                                }))} />
                    </FormContainer.Prefix>
                }
                <EmailSupportText className='email-support-text'>
                    Please check your spam folder if you don't see the email. Having trouble?
                </EmailSupportText>
                <SupportContentWrapper className='email-support-content'>
                    <div>
                        <Anchor className='reset-email-link'
                                onClick={() => handleResendEmail()}>
                            Re-send My Verification Email
                        </Anchor>
                    </div>
                    <div><Anchor href="/" className='start-over-link'>Start Over</Anchor></div>
                    <div><Anchor href="/support" className='support-link'>Parchment Support</Anchor></div>
                </SupportContentWrapper>
                <StyledRequiredSection>
                    <RequiredAsterisk />
                </StyledRequiredSection>
            </FormContainer>
        </BaseLayout>
    );
};

const AccountConfirmationInputContainer = styled(FormContainer)`
    text-align: center;
`;
AccountConfirmationInputContainer.displayName = 'AccountConfirmationInputContainer';

const StyledHeading4 = styled(Heading4)`
    color: ${dustGray};
`;
StyledHeading4.displayName = 'StyledHeading4';

const StyledHeading3 = styled(Heading3)`
    margin: 0 0 20px 0;
`;
StyledHeading3.displayName = 'StyledHeading3';

const NewMessageIconWrapper = styled.div`
    margin: 0 auto;
    width: 140px;

    > svg {
        height: 100%;
        width: 100%;

        linearGradient#a {
            stop {
                stop-color: ${java};
            }
        }

        linearGradient#b {
            stop {
                stop-color: ${atoll};
            }
        }
    }
`;
NewMessageIconWrapper.displayName = 'NewMessageIconWrapper';

const ConfirmationCodeForm = styled(Form)`
    margin: 40px 0;
`;
ConfirmationCodeForm.displayName = 'ConfirmationCodeForm';

const ConfirmationCodeInputWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-end;

    > div {
        flex: 1;
    }
`;

const SubmitButton = styled(Button)`
    @media (min-width: ${breakpoints.mediumMin}) {
        position: relative;
        right: 1px;
        bottom: 0.5px;
        min-width: 100px;
        max-width: 120px;
    }
`;
SubmitButton.displayName = 'SubmitButton';

const EmailSupportText = styled.div`
    text-align: center;
    padding-top: 40px;
`;
EmailSupportText.displayname = 'EmailSupportText';

const SupportContentWrapper = styled.div`
    display: flex;
    justify-content: center;
    flex-direction: row;
    flex-wrap: wrap;
    font-weight: 600;
    margin-top: 5px;

    > div {
        padding: 0 10px;
    }

    > div:not(:first-child) {
        border-left: 1px solid ${dustGray};
    }
`;
SupportContentWrapper.displayName = 'SupportContentWrapper';

const CancelLinkWrapper = styled.div`
    font-size: 18px;

    > a {
        text-decoration: none;
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        align-items: center;
        column-gap: 5px;

        > svg {
            font-size: 1.5em;
            margin-bottom: 5px;
        }
    }
`;
CancelLinkWrapper.displayName = 'CancelLinkWrapper';

const StyledRequiredSection = styled.div`
    padding-top: 40px;

    > div {
        justify-content: center;
    }
`;
StyledRequiredSection.displayName = 'StyledRequiredSection';
